import React, { useState } from 'react';
import { useBreakpointValue } from '@chakra-ui/media-query';
import { Flex, Box, VStack, Button } from '@chakra-ui/react';
import { FaTruckIcon } from '@outdoorly/icons';
import { Text } from '@outdoorly/text';
import { Card } from '@outdoorly/card';
import NextImage from 'next/image';
import { getBlurUrl, getImageLoader } from '@outdoorly/util';
import { Badge } from '@outdoorly/badge';

const MAX_DESCRIPTION_LENGTH = 400;

export type BrandHeroProps = {
  description: string;
  desktopHeroUrl: string;
  logoUrl: string;
  mobileHeroUrl: string;
  name: string;
  shippingDescription: string;
  tagline: string;
}
export const BrandHero: React.FC<BrandHeroProps> = ({
  mobileHeroUrl,
  desktopHeroUrl,
  name,
  tagline,
  description,
  shippingDescription,
  logoUrl,
}) => {
  const backgroundUrl = useBreakpointValue([
    mobileHeroUrl ?? desktopHeroUrl,
    null,
    desktopHeroUrl,
  ]);

  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  return (
    <Flex
      justify="center"
      backgroundColor="scheme.bgPrimaryInverse"
      position="relative"
    >
      {backgroundUrl && (
        <Box
          filter="brightness(70%)"
          position="absolute"
          top={0}
          bottom={0}
          right={0}
          left={0}
        >
          <NextImage
            loader={getImageLoader(backgroundUrl)}
            src={backgroundUrl}
            blurDataURL={getBlurUrl(backgroundUrl)}
            layout="fill"
            objectFit="cover"
            objectPosition="center center"
            sizes="100vw"
            quality={100}
            className="smooth-filter"
            placeholder="blur"
          />
        </Box>
      )}
      <VStack
        minH="sm"
        justify="center"
        align="center"
        padding={20}
        maxW="6xl"
        position="relative"
        spacing={8}
      >
        {logoUrl && (
          <Card
            width={24}
            height={24}
            padding={0}
            position="relative"
            overflow="hidden"
          >
            <NextImage
              loader={getImageLoader(logoUrl)}
              src={logoUrl}
              layout="fill"
              objectFit="contain"
              objectPosition="center center"
              sizes="100px"
            />
          </Card>
        )}
        <VStack spacing={4}>
          <Text
            as="h1"
            size="3xl"
            fontWeight="500"
            color="scheme.textPrimaryInverse"
          >
            {name}
          </Text>
          <Text
            as="h2"
            color="scheme.textPrimaryInverse"
            size="lg"
            textAlign="center"
          >
            {tagline}
          </Text>
        </VStack>
        {description && (
          <Text
            as="h3"
            color="scheme.textPrimaryInverse"
            size="lg"
            textAlign="center"
            maxW="full"
          >
            {isOpen
              ? description
              : description.slice(0, MAX_DESCRIPTION_LENGTH)}
            {description?.length > MAX_DESCRIPTION_LENGTH && (
              <>
                ...
                <Button
                  variant="link"
                  size="sm"
                  onClick={handleToggle}
                  colorScheme="white"
                  ml="md"
                >
                  Read {isOpen ? 'less' : 'more'}
                </Button>
              </>
            )}
          </Text>
        )}
        <Badge
          variant="subtle"
          colorScheme="teal"
          label={shippingDescription}
          leftIcon={
            <FaTruckIcon
              height={4}
              width={4}
              backgroundColor="teal.800"
              color="teal.100"
              boxSizing="content-box"
              borderColor="teal.800"
              borderWidth="3px"
              pl="1px"
              pt="1px"
              borderRadius="full"
              overflow="visible"
            />
          }
        />
      </VStack>
    </Flex>
  );
};
